// usePagination.js
import { useState, useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";

const usePaginateBookedEvent = (reduxSelector, fetchAction, fetchParams = {}) => {
  const dispatch = useDispatch();
  const reduxData = useSelector(reduxSelector);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(0);
  const [hasMore, setHasMore] = useState(true);
  const [initialized, setInitialized] = useState(false);
  const PAGE_SIZE = 10;  // Set your page size here

  // Merge new data with existing data
  useEffect(() => {
    if (Array.isArray(reduxData)) {
      setData(prev => {
        // Create a map to remove duplicates while preserving order
        const dataMap = new Map();
        
        [...prev, ...reduxData].forEach(item => {
          if (!dataMap.has(item.id)) {
            dataMap.set(item.id, item);
          }
        });
        
        return Array.from(dataMap.values());
      });
      
      // Determine if there's more data
      setHasMore(reduxData.length === PAGE_SIZE);
    }
  }, [reduxData]);

  

  const loadPage = useCallback(async (page) => {
    if (loading) return;
    
    try {
      setLoading(true);
      await dispatch(
        fetchAction({
          ...fetchParams,
          page,
        })
      );
      return true;
    } catch (error) {
      console.error("Pagination error:", error);
      setHasMore(false);
      return false;
    } finally {
      setLoading(false);
    }
  }, [dispatch, fetchAction, fetchParams, loading]);

  const handleLoadMore = useCallback(async () => {
    if (!hasMore || loading) return;
    
    const nextPage = currentPage + 1;
    const success = await loadPage(nextPage);
    
    if (success) {
      setCurrentPage(nextPage);
    }
  }, [currentPage, hasMore, loading, loadPage]);

  const handleRefresh = useCallback(async () => {
    setCurrentPage(0);
    setData([]);
    setHasMore(true);
    await loadPage(0);
  }, [loadPage]);

  // Initial load
  useEffect(() => {
    if (!initialized) {
      handleRefresh();
      setInitialized(true);
    }
  }, [handleRefresh, initialized]);

  return {
    data,
    loading,
    hasMore,
    handleRefresh,
    handleLoadMore,
    setData
  };
};

export default usePaginateBookedEvent;